import React, { useState, useEffect } from 'react';
import { AdminDashboard } from './AdminDashboard';
import { CreateBookingDialog, EditBookingDialog } from './BookingDialogs';
import { BookingCalendarView } from './BookingCalendarView';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { getBookings, updateBooking, deleteBooking, generateReceipt, createBookingDirect } from '../utils/api';
import { toast } from 'sonner';
import { Phone, Calendar as CalendarIcon, Trash2, Edit, Search, FileText, Plus, List, CalendarDays } from 'lucide-react';
// Calendar and Popover imports removed - not needed in list view
import { format } from 'date-fns';

export const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    status: '',
    notes: '',
    eventDate: null,
    eventEndDate: null,
    eventTimeFrom: '',
    eventTimeTo: '',
    amount: '',
    advancePaid: '',
    balanceDue: ''
  });
  const [createData, setCreateData] = useState({
    name: '',
    phone: '',
    eventDate: null,
    eventEndDate: null,
    eventType: '',
    eventTimeFrom: '07:00 AM',
    eventTimeTo: '08:00 PM',
    amount: '',
    advancePaid: '',
    balanceDue: '',
    notes: ''
  });

  const adminRole = localStorage.getItem('adminRole');

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [searchTerm, statusFilter, bookings]);

  const loadBookings = async () => {
    setIsLoading(true);
    try {
      const response = await getBookings();
      setBookings(response.bookings);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setIsLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = [...bookings];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(b => 
        b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.phone.includes(searchTerm) ||
        b.eventType.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredBookings(filtered);
  };

  const handleEdit = (booking) => {
    setSelectedBooking(booking);
    setEditData({
      status: booking.status,
      notes: booking.notes || '',
      eventDate: booking.eventDate || null,
      eventEndDate: booking.eventEndDate || null,
      eventTimeFrom: booking.eventTimeFrom || '07:00 AM',
      eventTimeTo: booking.eventTimeTo || '08:00 PM',
      amount: booking.amount || '',
      advancePaid: booking.advancePaid || '',
      balanceDue: booking.balanceDue || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!createData.name || !createData.phone || !createData.eventDate || !createData.eventType || !createData.amount) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const balanceDue = parseFloat(createData.amount) - parseFloat(createData.advancePaid || 0);
      const bookingData = {
        ...createData,
        eventDate: createData.eventDate, // Already in YYYY-MM-DD format from date input
        eventEndDate: createData.eventEndDate || null,
        amount: parseFloat(createData.amount),
        advancePaid: parseFloat(createData.advancePaid || 0),
        balanceDue: balanceDue,
        status: 'confirmed'
      };

      await createBookingDirect(bookingData);
      toast.success('Booking created successfully!');
      setIsCreateDialogOpen(false);
      setCreateData({
        name: '',
        phone: '',
        eventDate: null,
        eventType: '',
        eventTimeFrom: '07:00 AM',
        eventTimeTo: '08:00 PM',
        amount: '',
        advancePaid: '',
        balanceDue: '',
        notes: ''
      });
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    }
  };

  const handleUpdate = async () => {
    try {
      const updatePayload = {
        status: editData.status,
        notes: editData.notes
      };

      // Include date and payment fields if changed
      // Dates are already in YYYY-MM-DD format from date input
      if (editData.eventDate) {
        updatePayload.eventDate = editData.eventDate;
      }
      if (editData.eventEndDate) {
        updatePayload.eventEndDate = editData.eventEndDate;
      }
      if (editData.eventTimeFrom) updatePayload.eventTimeFrom = editData.eventTimeFrom;
      if (editData.eventTimeTo) updatePayload.eventTimeTo = editData.eventTimeTo;
      if (editData.amount) updatePayload.amount = parseFloat(editData.amount);
      if (editData.advancePaid) updatePayload.advancePaid = parseFloat(editData.advancePaid);

      await updateBooking(selectedBooking.id, updatePayload);
      toast.success('Booking updated successfully');
      setIsEditDialogOpen(false);
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    try {
      await deleteBooking(bookingId);
      toast.success('Booking deleted successfully');
      loadBookings();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const handleGenerateReceipt = async (booking) => {
    try {
      toast.info('Generating receipt...');
      const response = await generateReceipt(booking.id);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      window.open(`${backendUrl}${response.receiptUrl}`, '_blank');
      toast.success('Receipt generated successfully!');
    } catch (error) {
      toast.error('Failed to generate receipt');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminDashboard currentPage="bookings">
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Bookings Management</h2>
          <div className="flex flex-wrap gap-2">
            {/* View Toggle */}
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={`text-xs sm:text-sm ${viewMode === 'list' ? 'bg-[#D4AF37] text-black hover:bg-[#C19B2E]' : ''}`}
              >
                <List className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">List</span>
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                className={`text-xs sm:text-sm ${viewMode === 'calendar' ? 'bg-[#D4AF37] text-black hover:bg-[#C19B2E]' : ''}`}
              >
                <CalendarDays className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Calendar</span>
              </Button>
            </div>
            <Button onClick={loadBookings} variant="outline" size="sm" className="text-xs sm:text-sm">
              Refresh
            </Button>
            <Button onClick={() => setIsCreateDialogOpen(true)} size="sm" className="bg-[#D4AF37] text-black hover:bg-[#C19B2E] text-xs sm:text-sm">
              <CalendarIcon className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Create Booking</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </div>

        {/* Filters - Only show in list view */}
        {viewMode === 'list' && (
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow space-y-3 sm:space-y-0 sm:flex sm:items-center sm:gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 text-sm">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        )}

        {/* List View - Mobile Cards / Desktop Table */}
        {viewMode === 'list' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">Loading bookings...</div>
          ) : filteredBookings.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No bookings found</div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block sm:hidden divide-y divide-gray-200">
                {filteredBookings.map((booking) => (
                  <div key={booking.id} className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{booking.name}</p>
                        <button
                          onClick={() => handleCall(booking.phone)}
                          className="flex items-center text-blue-600 text-sm"
                        >
                          <Phone className="w-3 h-3 mr-1" />
                          {booking.phone}
                        </button>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <CalendarIcon className="w-3 h-3 mr-1" />
                      {format(new Date(booking.eventDate), 'MMM dd, yyyy')} • {booking.eventType}
                    </div>
                    <div className="flex gap-2 pt-2">
                      {booking.status === 'confirmed' && (
                        <Button
                          onClick={() => handleGenerateReceipt(booking)}
                          size="sm"
                          className="bg-[#D4AF37] text-black hover:bg-[#C19B2E] text-xs flex-1"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Receipt
                        </Button>
                      )}
                      {adminRole === 'admin' && (
                        <>
                          <Button
                            onClick={() => handleEdit(booking)}
                            variant="outline"
                            size="sm"
                            className="flex-1 text-xs"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleDelete(booking.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 text-xs"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event Date</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event Type</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-gray-50">
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900 text-sm">{booking.name}</div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleCall(booking.phone)}
                            className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <Phone className="w-4 h-4 mr-1" />
                            {booking.phone}
                          </button>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-gray-900 text-sm">
                            <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                            {format(new Date(booking.eventDate), 'MMM dd, yyyy')}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-gray-900 text-sm">{booking.eventType}</td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            {booking.status === 'confirmed' && (
                              <Button
                                onClick={() => handleGenerateReceipt(booking)}
                                size="sm"
                                className="bg-[#D4AF37] text-black hover:bg-[#C19B2E]"
                                title="Generate Receipt"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            )}
                            {adminRole === 'admin' && (
                              <>
                                <Button
                                  onClick={() => handleEdit(booking)}
                                  variant="outline"
                                  size="sm"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  onClick={() => handleDelete(booking.id)}
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        )}

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <BookingCalendarView
            bookings={bookings}
            onDateClick={handleEdit}
          />
        )}
      </div>

      <CreateBookingDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        createData={createData}
        setCreateData={setCreateData}
        handleCreate={handleCreate}
      />

      <EditBookingDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        booking={selectedBooking}
        editData={editData}
        setEditData={setEditData}
        handleUpdate={handleUpdate}
      />
    </AdminDashboard>
  );
};
